// Google Tag Manager Class to be injected
// TODO: check if port to https://nuxt.com/modules/nuxt-gtm is possible
// TODO: update with tracking-helper stuff

import type {
    GTMContext,
    GTMEcommerceItem,
    GTMEventPayload,
    GTMFormEventDetails,
    GTMFormEvents,
    GTMLanguageSwitchEvent,
    GTMModuleOptions,
    GTMSelectContentPayload,
    GTMSelectItemPayload,
    S2SGTMEventPayload,
} from '~/@types/gtm';
import type {
    CheckoutMutation_ManageCheckout_Checkout,
    CheckoutMutation_ManageCheckout_Checkout_Extended,
    CheckoutMutation_ManageCheckout_Checkout_Extended_LineItem,
    CheckoutMutation_ManageCheckout_Checkout_Extended_LineItems,
} from '~/@types/nestedGraphqlTypes';
import { CurrencyCode } from '~/graphql/generated';

// const searchEngines = [
//     'www.google',
//     'www.bing',
//     'www.yandex',
//     'search.yahoo',
//     'duckduckgo',
//     'www.ask',
//     'www.search',
//     'www.info',
//     'www.baidu',
//     'search.aol',
//     'www.dogpile',
// ];

export class GTM {
    ctx: GTMContext;
    options: GTMModuleOptions;
    constructor(ctx: GTMContext, options: GTMModuleOptions) {
        this.ctx = ctx;
        this.options = options;
        window.dataLayer = window.dataLayer || [];
        this.pushEvent({
            event: 'gtm.js',
            'gtm.start': new Date().getTime(),
        });
    }

    #getWindowLayer() {
        return window.dataLayer;
    }

    #numericPrice(maybeNumber?: number | string) {
        if (typeof maybeNumber === 'number') return maybeNumber;
        if (typeof maybeNumber === 'string') {
            try {
                return Number.parseFloat(maybeNumber);
            } catch (err) {}
        }
        return 0;
    }

    #parseTextPrice(price: string) {
        let bundlePrice = price.replace(/[^0-9.,]/gi, '');
        const dot = bundlePrice.indexOf('.');
        const comma = bundlePrice.indexOf(',');
        if (comma > 0) {
            if (dot > 0 && dot < comma) {
                bundlePrice = bundlePrice.replace(/[.]/gi, '').replace(/[,]/, '.');
            } else if (dot > 0 && dot > comma) {
                bundlePrice = bundlePrice.replace(/[,]/gi, '');
            } else {
                bundlePrice = bundlePrice.replace(/[,]/gi, '.');
            }
        }
        try {
            const ret = Number.parseFloat(bundlePrice);
            return Number.isNaN(ret) ? 0 : ret;
        } catch (err) {
            return 0;
        }
    }

    #calcBreadcrumbs(page: PageByUrlQuery_PageByUrl_Page) {
        if (page.breadcrumbs) {
            return page.breadcrumbs
                ?.map((b: any) => {
                    return b.breadcrumb?.replace(/(%\(|\)%|%{|}%|%\[|\]%)/gi, '');
                })
                .join(' > ');
        }
        return '';
    }

    /**
     * returns the item categories for the ecommerce object
     */
    #getItemCategories(category: string | null | undefined) {
        const categories: FixedLengthArray<[string, string]> = !category
            ? ['', '']
            : ['wow', 'original', 'go', 'explore', 'now', 'up', 'off'].includes(category.toLowerCase())
              ? ['Bikes', category]
              : [category, ''];
        return {
            item_category: categories[0],
            item_category2: categories[1],
            item_category3: '',
            item_category4: '',
            item_category5: '',
        };
    }

    #cmsProductToEcommerceObject(product: CMSProduct, index: number): GTMEcommerceItem {
        const currency: CurrencyCode = product.currency || CurrencyCode.Eur;
        const variant = product.variants.items[0];
        return {
            item_name: product.title,
            item_id: variant.sku,
            currency,
            price: product?.priceNumeric || 0,
            index,
            item_brand: 'woom',
            ...this.#getItemCategories(product.productType),
            // TODO: Could use product title + variant.selectedOptions https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#view_cart_item
            item_variant: '',
            // TODO: can we change item_image to image?
            item_image: product.image,
            quantity: 1,
            // Shopify properties and not available in CMS but required fields
            bundle_name: '',
            bundle_price: 0,
            discount: null,
            image: product.image || '',
            priceString: product?.priceNumeric?.toString() || '',
        };
    }

    #processCartItem(li: CheckoutMutation_ManageCheckout_Checkout_Extended_LineItem, idx: number, quantity: number): GTMEcommerceItem {
        let bundleName = '';
        let bundlePrice = 0;
        const bundlePriceDef = li.customAttributes.find((p: any) => p.key === '_bundle_price');
        if (bundlePriceDef) {
            bundlePrice = this.#parseTextPrice(bundlePriceDef.value);
        }
        const handle = li.variant?.productRef ? li.variant.productRef.substring(li.variant.productRef.lastIndexOf(':') + 1) : '';
        const bundleNameDef = li.customAttributes.find((p: any) => p.key === 'Bundle');
        if (bundleNameDef) {
            bundleName = bundleNameDef.value;
        }
        let discount = 0;
        // TODO: will have to change with the new storefront cart api
        if (Array.isArray(li.discountAllocations)) {
            for (const d of li.discountAllocations) {
                if (d.discountedAmount && d.discountedAmount.amount) {
                    const p = this.#parseTextPrice(d.discountedAmount.amount);
                    if (p) {
                        discount += p / (li.quantity || 1);
                    }
                }
            }
            discount = Math.round(discount * 100) / 100;
        }
        const price = this.#numericPrice(li.variant?.price?.amount || '');

        return {
            item_name: li.shopTitle || '',
            item_id: li.variant?.sku || '',
            currency: li.variant?.price?.currencyCode || '',
            price: price,
            priceString: typeof price === 'string' ? price : price?.toFixed(2),
            discount,
            quantity,
            image: li.variant?.shopImage || '',
            index: idx,
            // TODO: Bundle should be updated
            bundle_name: bundleName,
            bundle_price: bundlePrice,
            item_brand: 'woom',
            ...this.#getItemCategories(li.variant?.productType),
            item_variant: li.variant?.shopTitle === DEFAULT_TITLE ? '' : li.variant?.shopTitle || '',
        };
    }

    #ecommerceClear() {
        this.pushEvent({
            event: 'ecommerceClear',
            ecommerce: null,
        });
    }

    /**
     * When a user is presented with a list of results
     * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list
     */
    ecommerceViewItemList(listId: string, listName: string, products: CMSProduct[]) {
        if (!Array.isArray(products) || products.length === 0) return;
        const items = products.map((product) => {
            const price = this.#numericPrice(product.priceNumeric);
            const variant = product.variants.items[0]; // Pick the first variant
            return {
                item_id: variant?.sku, // TODO: Add fallback SKU for other product types
                item_name: product.title,
                price,
                ...this.#getItemCategories(product.productType),
            };
        });
        this.#ecommerceClear();
        this.pushEvent({
            event: 'view_item_list',
            ecommerce: {
                currency: products[0].currency,
                item_list_id: listId,
                item_list_name: listName,
                items,
            },
        });
    }

    /**
     * Enhanced measurement events
     * https://support.google.com/analytics/answer/9216061
     */
    ecommerceFormInteractions(event: GTMFormEvents, event_details: GTMFormEventDetails) {
        this.#ecommerceClear();
        this.pushEvent({
            event: event,
            ecommerce: {
                event_details: event_details,
            },
        });
    }

    ecommerceProductList(listId: string, listLabel: string, products: CMSProduct[]) {
        if (Array.isArray(products) && products.length > 0) {
            this.#ecommerceClear();
            this.pushEvent({
                event: 'view_item_list',
                ecommerce: {
                    currency: products[0].currency,
                    item_list_id: listId,
                    item_list_name: listLabel,
                    items: products.map(this.#cmsProductToEcommerceObject),
                },
            });
        }
    }

    ecommerceContentPage(page: PageByUrlQuery_PageByUrl_Page | undefined) {
        if (!page) return;
        this.pushEvent({
            event: 'emarsys_content',
            emarsys: {
                category: this.#calcBreadcrumbs(page),
            },
        });
    }

    ecommerceProductPage(page: PageByUrlQuery_PageByUrl_Page | undefined, product: CMSProduct | undefined) {
        if (!product || !page?.product) return;

        const variant = product.variants?.items?.find(isDefined);
        if (!variant) return;

        const price = page.product.priceRange?.minVariantPrice?.amount || '';
        const currency = page.product.priceRange?.minVariantPrice?.currencyCode || CurrencyCode.Eur;
        this.#ecommerceClear();
        this.pushEvent({
            event: 'productView',
            ecommerce: {
                currency,
                detail: {
                    actionField: { list: 'Product Detail Page' }, // Optional list property.
                    products: [
                        {
                            name: page.product.shopTitle || '',
                            id: variant.sku,
                            price,
                            brand: 'woom',
                            category: product.productType,
                            // TODO: Could use product title + variant.selectedOptions https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#view_cart_item
                            item_variant: '',
                            image: page.product?.shopImage || '',
                            categories: page.product.collections.map((collection) => collection?.handle).filter(isDefined) ?? [],
                        },
                    ],
                },
            },
        });
        this.#ecommerceClear();
        // now ga4
        this.pushEvent({
            event: 'view_item',
            ecommerce: {
                currency,
                value: this.#numericPrice(price),
                items: [this.#cmsProductToEcommerceObject(product, 0)],
            },
        });
        this.pushEvent({
            event: 'emarsys_pdp',
            emarsys: {
                category: this.#calcBreadcrumbs(page),
            },
        });
    }

    viewCart(cart: CheckoutMutation_ManageCheckout_Checkout) {
        if (!cart) return;
        this.#ecommerceClear();

        this.pushEvent({
            event: 'view_cart',
            ecommerce: {
                currency: cart.totalPrice?.currencyCode || CurrencyCode.Usd,
                value: this.#numericPrice(cart.totalPrice?.amount),
                items: cart.lineItems.map((lineItem, index) => this.#processCartItem(lineItem, index, lineItem.quantity || 1)),
            },
        });
    }

    cartPage(cart: CheckoutMutation_ManageCheckout_Checkout) {
        this.viewCart(cart);
        this.pushEvent({
            event: 'emarsys_cart',
            emarsys: {
                category: 'Cart',
            },
        });
    }

    // we only deal in single quantities when items are added
    addCartItems(
        items: CheckoutMutation_ManageCheckout_Checkout_Extended_LineItems,
        checkout: CheckoutMutation_ManageCheckout_Checkout,
        componentOrigin?: string,
    ) {
        if (!checkout) return;
        if (!Array.isArray(items) || items.length === 0) return;
        const currency = items[0].variant?.price?.currencyCode || '';
        const { newItems, price } = items.reduce(
            (accumulator, item, index) => {
                const data = this.#processCartItem(item, index, 1);
                const numericPrice = this.#numericPrice(data.price);
                let discount = 0;
                if (data.discount) {
                    discount = typeof data.discount === 'string' ? parseInt(data.discount) : data.discount;
                }
                accumulator.price += numericPrice - discount;
                accumulator.newItems.push(data);
                return accumulator;
            },
            { newItems: [] as GTMEcommerceItem[], price: 0 },
        );

        // adding all total items
        const allItems = checkout.lineItems.map((lineItem, index) => this.#processCartItem(lineItem, index, 1));
        const lineItemPrice = this.#numericPrice(checkout?.lineItemsSubtotalPrice?.amount);
        const total = this.#numericPrice(checkout?.totalPrice?.amount);
        let discount = 0;
        if (typeof lineItemPrice === 'number' && typeof total === 'number') {
            discount = Math.round((lineItemPrice - total + Number.EPSILON) * 100) / 100;
        }

        this.#ecommerceClear();
        this.pushEvent({
            event: 'add_to_cart',
            ecommerce: {
                currency,
                value: price,
                items: newItems,
                all: allItems,
                basket_size: checkout.totalQuantity,
                basket_value: total,
                basket_total_discount: discount,
                basket_original_total_price: lineItemPrice,
                checkoutUrl: checkout.webUrl,
                component_origin: componentOrigin,
            },
        } as GTMEventPayload);
        if (items?.length === 1) {
            const checkoutId = checkout.nativeCheckoutId ? checkout.nativeCheckoutId.substring('gid://shopify/Checkout/'.length) : checkout.id;
            const productID = getNativeVariantId(items[0].nativeProductId);
            this.pushEvent({
                event: 'add_triple_whale',
                tw: {
                    item: productID || '',
                    q: 1,
                    token: checkoutId,
                },
            });
        }
    }

    // we only deal with single quantities when items are removed
    removeCartItems(items: CheckoutMutation_ManageCheckout_Checkout_Extended_LineItems) {
        if (Array.isArray(items) && items.length > 0) {
            let price = 0;
            const currency = items[0].variant?.price?.currencyCode || '';
            let idx = 0;
            const newItems: GTMEcommerceItem[] = [];
            for (const cartItem of items) {
                const cartItemData = this.#processCartItem(cartItem, idx, 1);
                const numericPrice = this.#numericPrice(cartItemData.price);
                if (typeof numericPrice === 'number') {
                    price +=
                        numericPrice - (typeof cartItemData.discount === 'string' ? parseInt(cartItemData.discount) : cartItemData.discount || 0);
                }
                newItems.push(cartItemData);
                idx += 1;
            }
            this.#ecommerceClear();
            this.pushEvent({
                event: 'remove_from_cart',
                ecommerce: {
                    currency,
                    value: price,
                    items: newItems,
                },
            });
        }
    }

    /**
     * provides the cart object to the dataLayer
     */
    updateCart(objIn: CheckoutMutation_ManageCheckout_Checkout_Extended | null) {
        if (objIn) {
            this.pushEvent({
                event: 'cart-clear',
                cart: null,
            });
        }
        this.pushEvent({
            event: 'cart_update',
            cart: {
                items: (objIn?.lineItems || []).map((lineItem, index) => this.#processCartItem(lineItem, index, lineItem.quantity || 1)),
                basket_size: objIn?.totalQuantity ?? 0,
                basket_value: this.#numericPrice(objIn?.totalPrice?.amount),
                currency: objIn?.totalPrice ? objIn.totalPrice.currencyCode : null,
            },
        });
    }

    /**
     * Triggers GA4 standard event `select_content`
     *
     * 📘 [Event docs](https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#select_content)
     */
    selectContent(data: GTMSelectContentPayload) {
        this.pushEvent({
            event: 'select_content',
            ...data,
        });
    }
    /**
     * Triggers GA4 standard event `select_item`
     *
     * 📘 [Event docs](https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#select_item)
     */
    selectItem(ecommerce: GTMSelectItemPayload) {
        this.#ecommerceClear();
        this.pushEvent({
            event: 'select_item',
            ecommerce,
        });
    }

    async pushEvent(obj: GTMEventPayload) {
        try {
            if (!this.#getWindowLayer()) {
                throw new Error('missing GTM dataLayer');
            }
            if (typeof obj !== 'object') {
                throw new TypeError('event should be an object');
            }
            if ('ecommerce' in obj) {
                if (!obj.event) {
                    throw new Error('missing event property');
                }
            }

            this.#getWindowLayer().push(obj);

            const cloudflareWorkerUrl = this.ctx.cfCookieWorkerUrl;

            if (!cloudflareWorkerUrl) return;

            const s2sObj: S2SGTMEventPayload = obj;
            // S2S - sending data to the session cookie worker
            s2sObj.event_name = obj.event; // S2S uses "event_name" instead of "event"
            s2sObj.lang = this.ctx.lang;
            s2sObj.path = this.ctx.path;
            try {
                await $fetch(this.ctx.cfCookieWorkerUrl, {
                    method: 'POST',
                    body: s2sObj,
                    headers: { 'Content-type': 'application/json' },
                    credentials: 'include',
                });
            } catch (e) {
                console.error(`sending tracking data error: ${(e as Error).message ?? JSON.stringify(e)}`);
            }
        } catch (err) {
            console.error('[ERROR] [GTM]', err);
        }
    }
}

export default defineNuxtPlugin((nuxtApp) => {
    // in order to access the gtm config client side (including env variables), we need to access it via the
    // public configuration in the runtime config
    const config = useRuntimeConfig();
    const route = useRoute();

    const {
        $i18n: { locale },
    } = useNuxtApp();
    const ctx: any = { cfCookieWorkerUrl: config.public.cfCookieWorkerUrl, lang: locale.value.split('_')[0], path: route.path };

    const options: GTMModuleOptions = config.public?.gtm;
    if (!options) {
        console.log('gtm load - no options present: abort');
        return;
    }

    if (config.public.nodeEnv !== 'production' && !options.dev) {
        console.log('gtm - neither production nor a options.dev flag');
        return;
    }

    const gtm = new GTM(ctx, options);

    return {
        provide: {
            gtm,
        },
    };
});
